import type { FC } from 'react';
import { Suspense, useEffect, useMemo } from 'react';
import { RouterProvider } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { Spinner } from '@components/Spinner';
import { TooltipProvider } from '@components/ui/tooltip';
import { IconReplacement, toastClassName } from '@ContractBuilder/components/Notifications';
import { sentryErrorFallback } from '@pages/ErrorPage';
import { withErrorBoundary } from '@sentry/react';
import { useFeatureFlags } from '@src/hooks';
import { QueryClientProvider } from '@tanstack/react-query';
import { isPPLFrontend } from '@utils/app-paths';
import { queryClient } from '@utils/queryClient';
import { initStructuredClonePolyfill } from '@utils/structured-clone-polyfill';
import { VERSION_MISMATCH_TOAST_ID } from '@utils/VersionMismatchToast';
import axios from 'axios';

import { AxiosErrorInterceptor, AxiosResponseInterceptor } from './utils/axios-err-interceptor';
import { getAppRouter } from './utils/get-app-router';

initStructuredClonePolyfill();
axios.interceptors.response.use(AxiosResponseInterceptor, AxiosErrorInterceptor);

const App: FC = () => {
  const getFeatureFlagState = useFeatureFlags();
  const isBrandingEnabled = getFeatureFlagState('Branding');
  const isUsersEnabled = getFeatureFlagState('Users');
  const isTeamsEnabled = getFeatureFlagState('Teams');
  const isEvalConfigsEnabled = getFeatureFlagState('EvalConfigs');
  const isApiDocsEnabled = !isPPLFrontend;

  // Set data-hydrated attribute when component mounts (hydration complete)
  useEffect(() => {
    document.body.setAttribute('data-hydrated', 'true');
  }, []);

  const router = useMemo(
    () => getAppRouter(isUsersEnabled, isBrandingEnabled, isEvalConfigsEnabled, isApiDocsEnabled, isTeamsEnabled),
    [isUsersEnabled, isBrandingEnabled, isEvalConfigsEnabled, isApiDocsEnabled, isTeamsEnabled],
  );

  return (
    <TooltipProvider>
      <QueryClientProvider client={queryClient}>
        <Suspense fallback={<Spinner />}>
          <ToastContainer
            position="bottom-left"
            autoClose={5000}
            hideProgressBar
            closeOnClick
            pauseOnFocusLoss
            draggable
            pauseOnHover
            icon={IconReplacement}
            toastClassName={toastClassName}
          />
          <ToastContainer
            containerId={VERSION_MISMATCH_TOAST_ID}
            icon={IconReplacement}
            toastClassName={toastClassName}
            position="bottom-left"
            hideProgressBar
          />
          <RouterProvider router={router} />
        </Suspense>
      </QueryClientProvider>
    </TooltipProvider>
  );
};

export default withErrorBoundary(App, { fallback: sentryErrorFallback });
